import { ServerResponse } from 'http';

import { GetServerSidePropsContext } from 'next';
import pino, { Logger } from 'pino';

import { ConfigPublic } from '@/common/config.public';
import {
  AMAZON_TRACE_HEADER_NAME,
  CF_RAY_HEADER_NAME,
  CONNECTING_COUNTRY_CODE_HEADER_NAME,
  CONNECTING_IP_HEADER_NAME,
  COUNTRY_CODE_COOKIE_NAME,
  APP_VERSION_HEADER_NAME,
} from '@/common/constants/httpHeaders';
import { showGitInfo } from '@/common/server/showGitInfo';
import { GitInfo, getGitVersion } from '@/common/utils/git/GitInfo';

export const capitalize = (string: string) => string.charAt(0).toUpperCase() + string.slice(1);
export const safeArrayGet = <T>(array: T[], index: number): T | undefined => {
  if (index >= 0 && index < array.length) {
    return array[index];
  }
  return undefined;
};

export interface RequestContext {
  traceId?: string;
  transactionId?: string;
  clientIp?: string;
  countryHeader?: string;
  countryCookie?: string;
}

export const elysiumLogger = (ctx?: RequestContext, name?: string): Logger => {
  return pino({
    name: name ?? 'elysium',
    level: ConfigPublic.LogLevel,
    messageKey: 'message',
    timestamp: () => `,"timestamp":"${new Date(Date.now()).toISOString()}"`,
    mixin: () => {
      if (!ctx) {
        return {};
      }
      const { traceId, transactionId, ...fields } = ctx;
      Object.assign(fields, {
        'trace.id': traceId ?? 'none',
        'transaction.id': transactionId ?? 'none',
      });
      return fields;
    },
  });
};

const httpHeaderToString = (value: undefined | string | string[]): string | undefined => {
  if (Array.isArray(value)) {
    return value.at(0) ?? undefined;
  }
  return value;
};

export const getCountryCodeFromCookie = (
  cookies: Partial<Record<string, string>>,
): string | undefined => {
  if (!cookies) {
    return undefined;
  }

  return cookies[COUNTRY_CODE_COOKIE_NAME] || undefined;
};

export const headersToContext = (
  cookies: Partial<Record<string, string>>,
  inHeaders: Record<string, undefined | string | string[]>,
): RequestContext => {
  const rndId = randomId();
  return {
    traceId: httpHeaderToString(inHeaders[AMAZON_TRACE_HEADER_NAME]) ?? rndId,
    transactionId: httpHeaderToString(inHeaders[CF_RAY_HEADER_NAME]) ?? rndId,
    clientIp: httpHeaderToString(inHeaders[CONNECTING_IP_HEADER_NAME]),
    countryCookie: getCountryCodeFromCookie(cookies),
    countryHeader: httpHeaderToString(inHeaders[CONNECTING_COUNTRY_CODE_HEADER_NAME]),
  };
};

const randomId = (): string => {
  return Math.floor(Math.random() * 100000000).toString();
};

export const trimString = (str: string, maxLength: number): string => {
  if (!str) {
    return '';
  }

  if (maxLength <= 3) {
    return str.slice(0, maxLength);
  }

  if (str.length > maxLength) {
    return `${str.slice(0, maxLength - 3)}...`;
  }
  return str;
};

export const logDebug = (debug: string) => elysiumLogger({}, 'displate').debug(debug);
export const logWarn = (warn: string) => elysiumLogger({}, 'displate').warn(warn);
export const logError = (error: string) => elysiumLogger({}, 'displate').error(error);

export const isRetinaDevice = () => typeof window !== 'undefined' && window.devicePixelRatio > 1;

export const createAppropriatePathForDevice = (
  extension: 'jpg' | 'webp',
  isDesktop = false,
  isRetina = false,
  url: string,
) => {
  const suffix = isRetina ? 'x2' : 'x1';
  return isDesktop ? `${url}_${suffix}.${extension}` : `${url}_mobile_${suffix}.${extension}`;
};
export const getNumberFromString = (string: string | undefined) =>
  string ? Number(string.match(/\d+/)) : NaN;

// Please remember that on localhost this function will return 'US' as a default value
// since there is no Cloudflare proxy in front of localhost
export const selectCountryCode = (context: GetServerSidePropsContext): string => {
  const countryHeader = context.req.headers[CONNECTING_COUNTRY_CODE_HEADER_NAME] ?? 'US';
  const countryCookie = getCountryCodeFromCookie(context.req.cookies);
  const countryQuery = context.query.ctrc as string; // TODO: create a dedicated url query interface to handle ctrc

  return countryQuery || countryCookie || (countryHeader as string);
};

export const parseIdFromWebRequest = (id: string | undefined): number | null => {
  const parsedId = Number(id);
  if (isNaN(parsedId)) {
    return null;
  }
  if (parsedId.toString() !== id) {
    return null;
  }
  return parsedId;
};

export const getVersionInfoWhenNeeded = async (): Promise<GitInfo | null> => {
  if (!showGitInfo()) {
    return null;
  }

  try {
    return await getGitVersion();
  } catch {
    return null;
  }
};

export const compileArrayToPairs = <ItemType>(array: ItemType[]) =>
  array.reduce(
    (acc, curr, index) => {
      if (index % 2 === 0) {
        acc.push([curr, array[index + 1]]);
      }
      return acc;
    },
    [] as [ItemType, ItemType][],
  );

/**
 * Disables cache in getServerSideProps response, use it like: DisableCache(context.res);
 * @param response response in SSR context
 */
export const DisableCache = (response: ServerResponse) => {
  response.setHeader('Cache-Control', 'no-cache, no-store, must-revalidate');
  response.setHeader('Pragma', 'no-cache');
  response.setHeader('Expires', '0');
};

export const addVersionToHeader = (response: ServerResponse) => {
  const versionInfo = ConfigPublic.BuildVersion;
  if (versionInfo) {
    response.setHeader(APP_VERSION_HEADER_NAME, versionInfo);
  }
};
